import { useState, useRef, useEffect, ReactNode } from "react";

interface MapViewportProps {
  children: ReactNode;
  mapWidth: number;
  mapHeight: number;
}

export const MapViewport = ({
  children,
  mapWidth,
  mapHeight,
}: MapViewportProps) => {
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Функция для ограничения перемещения границами карты
  const constrainPan = (x: number, y: number) => {
    if (!containerRef.current) return { x, y };

    const container = containerRef.current;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    // Ограничиваем перемещение так, чтобы карта не выходила за границы viewport
    const maxX = 0;
    const minX = Math.min(0, containerWidth - mapWidth);
    const maxY = 0;
    const minY = Math.min(0, containerHeight - mapHeight);

    return {
      x: Math.max(minX, Math.min(maxX, x)),
      y: Math.max(minY, Math.min(maxY, y)),
    };
  };

  // Обработка начала перетаскивания (мышь)
  const handleMouseDown = (e: React.MouseEvent) => {
    // Игнорируем правую кнопку мыши
    if (e.button !== 0) return;

    setIsDragging(true);
    setDragStart({
      x: e.clientX - pan.x,
      y: e.clientY - pan.y,
    });
  };

  // Обработка перемещения мыши
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;

    const newPan = constrainPan(
      e.clientX - dragStart.x,
      e.clientY - dragStart.y
    );
    setPan(newPan);
  };

  // Обработка окончания перетаскивания
  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Обработка начала перетаскивания (тач)
  const handleTouchStart = (e: React.TouchEvent) => {
    // Если одно касание - начинаем drag
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      setIsDragging(true);
      setDragStart({
        x: touch.clientX - pan.x,
        y: touch.clientY - pan.y,
      });
    }
  };

  // Обработка перемещения пальца
  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 1 && isDragging) {
      const touch = e.touches[0];
      const newPan = constrainPan(
        touch.clientX - dragStart.x,
        touch.clientY - dragStart.y
      );
      setPan(newPan);
    }
  };

  // Обработка окончания касания
  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Предотвращаем выделение текста при перетаскивании
  useEffect(() => {
    if (isDragging) {
      document.body.style.userSelect = "none";
      document.body.style.cursor = "grabbing";
    } else {
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    }

    return () => {
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
  }, [isDragging]);

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full overflow-hidden"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        cursor: isDragging ? "grabbing" : "grab",
        touchAction: "none", // Отключаем стандартные жесты браузера
      }}
    >
      {/* Контент с трансформацией */}
      <div
        ref={contentRef}
        style={{
          transform: `translate(${pan.x}px, ${pan.y}px)`,
          transformOrigin: "0 0",
          transition: isDragging ? "none" : "transform 0.1s ease-out",
          willChange: "transform",
        }}
      >
        {children}
      </div>
    </div>
  );
};
